// @ts-nocheck
// AudioPlayer — sticky, library-backed audio player for chapter narration.
//
// Built on react-h5-audio-player (1.4k★, MIT, actively maintained) instead
// of a hand-rolled <audio> element + custom controls. The library gives us
// out of the box:
//   - A progress bar that genuinely supports click-to-seek and drag-to-seek
//   - Play/pause as the de-facto "stop" button (always one click away)
//   - Skip-back / skip-forward 15s buttons (great for re-listening to a
//     paragraph without hunting on the bar)
//   - Time displays that match the bar position
//
// On top of the library we add:
//   - position:sticky so the player stays visible at the top of the screen
//     while the user scrolls through chapter prose — they can pause / scrub
//     from anywhere without scrolling back to the player
//   - MediaSession integration so OS-level lock-screen controls work
//     (lock-screen play/pause/scrub, notification "now playing", audio
//     keeps playing while screen is off — the original reason the
//     previous SpeechSynthesis approach was useless on mobile)
//   - localStorage position persistence keyed per chapter, with restore
//     on mount and writes on pause / seek / time-tick / page-hide
//
// Notes for future maintainers:
//   - react-h5-audio-player exposes ref.audio.current for the underlying
//     HTMLAudioElement; we use it for MediaSession, restore, persist.
//   - The library's default theme is generic gray; the rhap_* CSS overrides
//     in src/App.tsx's `styles` block reshape it to the book's warm palette.

import { memo, useEffect, useRef } from "react";
import H5AudioPlayer from "react-h5-audio-player";
import "react-h5-audio-player/lib/styles.css";

type Props = {
  src: string;
  title: string;
  artist?: string;
  album?: string;
  // Unique key for storing playback position in localStorage. Different
  // chapters (or different audio sources) should use different keys so
  // they don't overwrite each other.
  storageKey: string;
  // Cloud-side resume position in seconds, supplied by useCloudProgress
  // via App.tsx. Spotify-style cross-device pickup: if a paid reader was
  // listening on phone, they resume here on laptop. We take max(local,
  // cloud) on mount so no progress is lost either way. Undefined/null
  // means no cloud value (free-preview readers, or new chapter).
  cloudPosition?: number | null;
  // Called when the player wants to persist a new position to the cloud.
  // Throttled internally — at most one call every PERSIST_CLOUD_EVERY_MS
  // during play, plus immediate calls on pause / seek / end / page-hide.
  onCloudPositionChange?: (positionSec: number) => void;
  // Optional "seek straight to section N" instruction. When the reader
  // taps a TOC subsection like 6.4 and then enters listen mode, the
  // parent sets this to 3 (4th section, 0-indexed) and we fetch
  // /chapter-<chapter>-sections.json to look up startSec, override the
  // localStorage/cloud restore, and start playing from there.
  chapter?: number;
  seekToSectionIdx?: number | null;
  // Fires when we've successfully applied the section seek. The parent
  // uses this to clear its pending state so a later manual rewind isn't
  // overridden.
  onSectionSeekConsumed?: () => void;
  // Fires every ~listenInterval (2s) with the audio's current playback
  // time in seconds. App.tsx uses this to drive the "auto-scroll the
  // page to the section currently being narrated" feature.
  onTimeUpdate?: (positionSec: number) => void;
  // Optional extra UI rendered inside the player's title row — used for
  // the auto-scroll toggle. Kept as a render prop so App.tsx can wire
  // up state without AudioPlayer needing to know about it.
  titleSlot?: React.ReactNode;
  // External pause/play command. App.tsx sets this object with a new
  // `token` value whenever it wants AudioPlayer to start or stop
  // playback (e.g. the exercise auto-pause for the listening-redesign
  // pilot). We dedupe via the token so re-renders with the same
  // command don't double-fire.
  command?: { action: "play" | "pause" | "restart"; token: number } | null;
};

// Throttle for cloud position writes during continuous playback. The
// useCloudProgress hook adds its own 1.5s debounce, so the effective
// minimum gap between writes is ~max(this, 1500ms).
const PERSIST_CLOUD_EVERY_MS = 5000;

function AudioPlayerImpl({
  src,
  title,
  artist = "שאלת האריה — סדנה בספר",
  album = "שלמה אלפנבוים",
  storageKey,
  cloudPosition,
  onCloudPositionChange,
  chapter,
  seekToSectionIdx,
  onSectionSeekConsumed,
  onTimeUpdate,
  titleSlot,
  command,
}: Props) {
  // Ref into the underlying H5AudioPlayer instance. .audio.current is the
  // real HTMLAudioElement.
  const playerRef = useRef<H5AudioPlayer | null>(null);
  // Last localStorage-write timestamp, for throttling on time-tick.
  const lastPersistRef = useRef<number>(0);
  // Last cloud-write timestamp. Separate from localStorage because cloud
  // writes are heavier (network) — we throttle them at PERSIST_CLOUD_EVERY_MS
  // while playing, but always fire on pause / seek / end / page-hide.
  const lastCloudPersistRef = useRef<number>(0);
  // Pin onCloudPositionChange in a ref so persist helpers don't need
  // to be re-memoised whenever the parent re-renders.
  const cloudCbRef = useRef(onCloudPositionChange);
  cloudCbRef.current = onCloudPositionChange;

  const getAudio = (): HTMLAudioElement | null => {
    const p = playerRef.current as any;
    return p?.audio?.current || null;
  };

  // Persist position. Always writes localStorage (cheap, offline-safe).
  // Optionally pushes to cloud — caller passes `force=true` for events
  // that should never be throttled (pause / seek / end / page-hide).
  const persistPosition = (force = false) => {
    const audio = getAudio();
    if (!audio) return;
    const pos = audio.currentTime;
    try {
      window.localStorage.setItem(storageKey, String(pos));
      lastPersistRef.current = Date.now();
    } catch {}
    if (cloudCbRef.current && Number.isFinite(pos) && pos > 0) {
      const now = Date.now();
      if (force || now - lastCloudPersistRef.current >= PERSIST_CLOUD_EVERY_MS) {
        cloudCbRef.current(pos);
        lastCloudPersistRef.current = now;
      }
    }
  };

  // Track what we've restored for so the effect doesn't fight itself.
  // Keyed on the src+sectionIdx pair we last acted on. The trick: when
  // we successfully seek to a section, the PARENT may clear its
  // pendingAudioSection state, which flips the seekToSectionIdx prop
  // from 4 back to null and re-triggers this effect. If we naively
  // fall through to restoreFromSaved on the null branch, it CLOBBERS
  // our seek with localStorage's last-paused position. So we treat the
  // "section idx was set, now cleared" transition as "no new intent —
  // leave the audio where it is."
  const lastRestoreRef = useRef<{ src: string; sectionIdx: number | null } | null>(null);
  // Set (per src) when the reader hits "חזרה להתחלה" — the restart must
  // WIN over the saved/cloud position restore. Round-15 bug (Shlomo):
  // restart pressed before metadata loaded → the seek-to-0 was dropped,
  // then loadedmetadata fired restoreFromSaved which jumped to the SAVED
  // position — the END of an already-finished chapter.
  const restartedForSrcRef = useRef<string | null>(null);
  // Stash callbacks in a ref so the restore effect doesn't have to
  // re-run every time the parent re-renders.
  const sectionSeekConsumedRef = useRef(onSectionSeekConsumed);
  sectionSeekConsumedRef.current = onSectionSeekConsumed;

  // Restore saved position once metadata is known. The library fires
  // onLoadedMetaData reliably, but on a fast cache hit metadata may
  // already be available by the time this component mounts — so we also
  // check audio.readyState directly on mount.
  //
  // Restore priority (highest wins):
  //   1. seekToSectionIdx — if the reader picked a TOC subsection,
  //      we fetch /chapter-N-sections.json, look up startSec for that
  //      section, and jump there. Also auto-plays. Overrides 2 + 3.
  //   2. max(localStorage, cloudPosition) — Spotify-style cross-device
  //      resume. A reader who was offline and listened further doesn't
  //      lose progress to a stale cloud value, and a new device with no
  //      local cache still picks up where the cloud left off.
  useEffect(() => {
    const audio = getAudio();
    if (!audio) return;

    let cancelled = false;

    const restoreFromSaved = () => {
      // A pending restart outranks any saved/cloud position.
      if (restartedForSrcRef.current === src) {
        restartedForSrcRef.current = null;
        try { audio.currentTime = 0; } catch {}
        return;
      }
      let target = 0;
      try {
        const saved = parseFloat(window.localStorage.getItem(storageKey) || "0");
        if (Number.isFinite(saved) && saved > 0) target = saved;
      } catch {}
      if (
        typeof cloudPosition === "number" &&
        Number.isFinite(cloudPosition) &&
        cloudPosition > target
      ) {
        target = cloudPosition;
      }
      if (target > 0 && target < (audio.duration || Infinity)) {
        try { audio.currentTime = target; } catch {}
      }
    };

    const restoreFromSection = async (idx: number, ch: number): Promise<boolean> => {
      try {
        const res = await fetch(`/chapter-${ch}-sections.json`, { cache: "no-cache" });
        if (!res.ok) return false; // no alignment for this chapter — fall back
        const data = await res.json();
        if (cancelled) return true;
        const section = (data?.sections || []).find((s: any) => s?.idx === idx);
        const startSec = section?.startSec;
        if (typeof startSec !== "number" || !Number.isFinite(startSec) || startSec < 0) {
          // Low-confidence section (startSec=null) or out-of-range idx.
          // Let the saved-position fallback take over.
          return false;
        }
        const duration = audio.duration || Infinity;
        try {
          audio.currentTime = Math.min(startSec, Math.max(0, duration - 1));
        } catch {}
        // The reader explicitly asked to listen from this section — start
        // playing. Autoplay blocks (e.g. iOS without a user gesture) fail
        // silently; the position is already set, so a manual tap plays
        // from the right spot.
        audio.play().catch(() => {});
        return true;
      } catch {
        return false;
      }
    };

    const restore = async () => {
      // Normalise the prop into a stable comparison key.
      const sectionIdx =
        typeof seekToSectionIdx === "number" && seekToSectionIdx >= 0
          ? seekToSectionIdx
          : null;
      const last = lastRestoreRef.current;
      if (last && last.src === src) {
        // Exact-same intent — nothing new to do.
        if (last.sectionIdx === sectionIdx) return;
        // Previously seeked to a section, now the parent has cleared the
        // pending state (sectionIdx flipped to null). If we ran the
        // saved-position branch here it would clobber the section seek
        // with the reader's last-paused position. Keep the audio where
        // it is.
        if (last.sectionIdx !== null && sectionIdx === null) return;
      }
      lastRestoreRef.current = { src, sectionIdx };

      if (sectionIdx !== null && typeof chapter === "number") {
        const ok = await restoreFromSection(sectionIdx, chapter);
        if (ok || cancelled) return;
        // Section seek wasn't possible (missing JSON, low-confidence
        // alignment, network error) — fall through to the saved-position
        // restore as a graceful default.
      }
      restoreFromSaved();
    };

    if (audio.readyState >= 1) {
      restore();
    } else {
      audio.addEventListener("loadedmetadata", restore);
    }
    return () => {
      cancelled = true;
      audio.removeEventListener("loadedmetadata", restore);
    };
  }, [storageKey, src, cloudPosition, chapter, seekToSectionIdx]);

  // MediaSession metadata + action handlers — lock-screen / notification
  // controls. setActionHandler is wrapped in try/catch because action
  // support varies across browsers (e.g. seekto isn't on every iOS).
  useEffect(() => {
    if (typeof window === "undefined" || !("mediaSession" in navigator)) return;
    try {
      // @ts-ignore - MediaMetadata is a global constructor
      navigator.mediaSession.metadata = new window.MediaMetadata({
        title,
        artist,
        album,
      });
    } catch {}

    const audio = getAudio();
    if (!audio) return;
    const ms: any = navigator.mediaSession;

    try { ms.setActionHandler("play", () => { audio.play().catch(() => {}); }); } catch {}
    try { ms.setActionHandler("pause", () => audio.pause()); } catch {}
    try {
      ms.setActionHandler("seekto", (details: any) => {
        if (details && typeof details.seekTime === "number") {
          audio.currentTime = details.seekTime;
        }
      });
    } catch {}
    try {
      ms.setActionHandler("seekforward", (details: any) => {
        audio.currentTime = Math.min(
          audio.duration || 0,
          audio.currentTime + (details?.seekOffset || 15),
        );
      });
    } catch {}
    try {
      ms.setActionHandler("seekbackward", (details: any) => {
        audio.currentTime = Math.max(0, audio.currentTime - (details?.seekOffset || 15));
      });
    } catch {}

    return () => {
      ["play", "pause", "seekto", "seekforward", "seekbackward"].forEach((a) => {
        try { ms.setActionHandler(a, null); } catch {}
      });
    };
  }, [title, artist, album, src]);

  // Execute external play/pause commands from the parent. App.tsx uses
  // this for the exercise auto-pause flow: when the audio crosses a
  // structured-exercise marker, App.tsx sends { action: 'pause', token }
  // and we pause the underlying <audio>. The reader's "המשך האזנה"
  // button on the overlay sends { action: 'play', token: <new> } and
  // we resume.
  //
  // Dedup via token so a re-render with the same command doesn't
  // re-execute it. Tracks the LAST applied token in a ref.
  const lastCommandTokenRef = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (!command) return;
    if (lastCommandTokenRef.current === command.token) return;
    lastCommandTokenRef.current = command.token;
    const audio = getAudio();
    if (!audio) return;
    if (command.action === "pause") {
      try { audio.pause(); } catch {}
    } else if (command.action === "play") {
      try { audio.play().catch(() => {}); } catch {}
    } else if (command.action === "restart") {
      // Seek to the top of the chapter, preserving play/pause state.
      // Must beat the saved-position restore on all fronts: flag a
      // pending restart for the metadata-not-ready case, zero the
      // saved positions (local + cloud) so no later restore — on this
      // device or another — resurrects the old spot.
      restartedForSrcRef.current = src;
      try { window.localStorage.setItem(storageKey, "0"); } catch {}
      try { cloudCbRef.current?.(0); } catch {}
      try {
        if (audio.readyState >= 1) {
          audio.currentTime = 0;
          restartedForSrcRef.current = null;
        }
      } catch {}
    }
  }, [command]);

  // Save on tab-hide / page-close (e.g. user backgrounds the app, closes
  // the laptop lid, or the OS swaps the tab out of memory). pagehide is
  // the modern catch-all; visibilitychange catches the in-page swap.
  // `force=true` so the cloud write isn't suppressed by the play-time
  // throttle — these events are rare and the position matters most here.
  useEffect(() => {
    const onHide = () => persistPosition(true);
    document.addEventListener("visibilitychange", onHide);
    window.addEventListener("pagehide", onHide);
    return () => {
      document.removeEventListener("visibilitychange", onHide);
      window.removeEventListener("pagehide", onHide);
    };
  }, [storageKey]);

  return (
    <div className="ap2-sticky">
      <div className="ap2-title">
        <span className="ap2-title-text">{title}</span>
        {titleSlot ? <span className="ap2-title-slot">{titleSlot}</span> : null}
      </div>
      <H5AudioPlayer
        ref={playerRef}
        src={src}
        preload="metadata"
        // Skip-back/skip-forward 15s buttons; "skip" (prev/next track) hidden
        // because we don't have a playlist concept.
        showJumpControls
        showSkipControls={false}
        progressJumpSteps={{ backward: 15000, forward: 15000 }}
        // Hide loop button (irrelevant for a chapter) and the volume slider
        // (mobile users use system volume; desktop users can use the audio
        // element's contextual right-click controls if needed).
        customAdditionalControls={[]}
        customVolumeControls={[]}
        // The library's "horizontal" layout puts progress + controls on one
        // row with the bar at the top — clean and compact for a sticky bar.
        layout="horizontal"
        // Tighten the onListen heartbeat so we persist position every ~2s
        // of playback (instead of the library's 1s default — keeps writes
        // to localStorage modest while remaining responsive on crash).
        listenInterval={2000}
        onPlay={() => {
          if ("mediaSession" in navigator) {
            (navigator.mediaSession as any).playbackState = "playing";
          }
        }}
        onPause={() => {
          // Force cloud write — pause is a strong signal the reader is
          // stopping for a while, and the position here is what they
          // expect to come back to.
          persistPosition(true);
          if ("mediaSession" in navigator) {
            (navigator.mediaSession as any).playbackState = "paused";
          }
        }}
        onSeeked={() => {
          // Force on seek too — the new position is exactly the value the
          // reader intends, and the play-time throttle would otherwise
          // swallow it.
          persistPosition(true);
        }}
        onListen={() => {
          // listenInterval handles the local throttle; cloud writes are
          // gated by PERSIST_CLOUD_EVERY_MS inside persistPosition.
          persistPosition();
          // Notify parent of current playback time so it can drive
          // auto-scroll of the page to the section being narrated.
          if (onTimeUpdate) {
            const audio = getAudio();
            if (audio && Number.isFinite(audio.currentTime)) {
              onTimeUpdate(audio.currentTime);
            }
          }
        }}
        onEnded={() => {
          try { window.localStorage.removeItem(storageKey); } catch {}
          // Note: we deliberately do NOT clear the cloud __audioPos here.
          // With the current upsert-without-delete semantics in
          // useCloudProgress, writing 0 wouldn't actually wipe the row's
          // existing __audioPos; the next device would still seek to the
          // last saved position near the end. Acceptable for v1 — reader
          // hears the last few seconds, then it ends naturally. If this
          // becomes annoying, follow-up work can add a real "clear field"
          // operation to useCloudProgress.
        }}
      />
    </div>
  );
}

// Memoized so the parent's ~2s playback-time state updates (which drive the
// auto-scroll + exercise-pause effects in App.tsx) don't re-render the
// player on every tick. That re-render churn was making the library's
// pause button intermittently swallow clicks — play worked first try,
// pause took several. With stable props from App, currentAudioTime ticks
// no longer reconcile this subtree, so the controls stay responsive.
export const AudioPlayer = memo(AudioPlayerImpl);
